import { useState, useEffect, useCallback } from 'react';
import { weddingConfig } from '@/data/weddingConfig';
import ScrollReveal from '@/components/feature/ScrollReveal';

interface TimeLeft {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

function calculateTimeLeft(targetDate: string): TimeLeft | null {
  const difference = new Date(targetDate).getTime() - new Date().getTime();
  if (difference <= 0) return null;

  return {
    days: Math.floor(difference / (1000 * 60 * 60 * 24)),
    hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
    minutes: Math.floor((difference / 1000 / 60) % 60),
    seconds: Math.floor((difference / 1000) % 60),
  };
}

export default function Countdown() {
  const [timeLeft, setTimeLeft] = useState<TimeLeft | null>(
    calculateTimeLeft(weddingConfig.countdownTargetDate)
  );

  const updateTimer = useCallback(() => {
    setTimeLeft(calculateTimeLeft(weddingConfig.countdownTargetDate));
  }, []);

  useEffect(() => {
    const timer = setInterval(updateTimer, 1000);
    return () => clearInterval(timer);
  }, [updateTimer]);

  const isWeddingDay = timeLeft === null;

  return (
    <section className="relative w-full bg-background-50 py-14 md:py-20">
      <div className="mx-auto max-w-3xl px-6 text-center">
        <ScrollReveal>
          <p className="font-heading text-2xl italic text-foreground-700 sm:text-3xl md:text-4xl">
            {isWeddingDay ? weddingConfig.countdownCompleteMessage : 'Faltan'}
          </p>
        </ScrollReveal>

        {!isWeddingDay && timeLeft && (
          <ScrollReveal delay={1}>
            <div className="mx-auto mt-12 flex max-w-xl items-stretch justify-between md:mt-16">
              {[
                { value: timeLeft.days, label: 'Días' },
                { value: timeLeft.hours, label: 'Horas' },
                { value: timeLeft.minutes, label: 'Minutos' },
                { value: timeLeft.seconds, label: 'Segundos' },
              ].map((unit, i) => (
                <div key={unit.label} className="flex flex-1 items-stretch justify-center">
                  <div className="flex flex-col items-center px-2 sm:px-3">
                    <span className="font-heading text-4xl font-light leading-none text-foreground-800 sm:text-5xl md:text-6xl lg:text-7xl">
                      {String(unit.value).padStart(2, '0')}
                    </span>
                    <span className="mt-4 font-body text-[10px] uppercase tracking-[0.25em] text-foreground-500">
                      {unit.label}
                    </span>
                  </div>
                  {i < 3 && (
                    <span className="mx-1 w-px self-center bg-foreground-200 sm:mx-2" style={{ height: '60px' }} />
                  )}
                </div>
              ))}
            </div>
          </ScrollReveal>
        )}

        {!isWeddingDay && (
          <ScrollReveal delay={2}>
            <div className="mt-14 flex items-center justify-center gap-4 md:mt-16">
              <span className="h-px w-16 bg-primary-300" />
              <span className="h-1.5 w-1.5 rotate-45 bg-primary-400" />
              <span className="h-px w-16 bg-primary-300" />
            </div>
          </ScrollReveal>
        )}
      </div>
    </section>
  );
}